import React from "react";
import { OWGameResponse } from "@devour/client";
import { FaClock, FaExternalLinkAlt, FaNewspaper } from "react-icons/fa";

interface GameNewsSectionProps {
    game: OWGameResponse;
}

const GameNewsSection: React.FC<GameNewsSectionProps> = ({ game }) => {
    // Check if we have Steam news - Steam API returns nested structure
    const rawNewsItems = game.steamNews?.newsitems?.newsitem || [];

    // Filter out any null/undefined items to prevent runtime errors
    const newsItems = rawNewsItems.filter((item: any) => {
        if (!item) {
            return false;
        }
        if (!item.url) {
            return false;
        }
        if (!item.title) {
            return false;
        }
        return true;
    });

    if (!newsItems || newsItems.length === 0) {
        // Don't show the news section if there's no news
        return null;
    }

    // Helper function to format the date
    const formatTimeAgo = (timestamp: number) => {
        const date = new Date(timestamp * 1000); // Convert from Unix timestamp
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHours / 24);

        if (diffHours < 1) return "Just now";
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

        return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    // Helper function to clean up HTML content from Steam
    const cleanContent = (content: string) => {
        // Safely parse HTML and extract text content
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, "text/html");
        let text = doc.body.textContent || "";
        // Limit length and add ellipsis if needed
        if (text.length > 150) {
            text = `${text.substring(0, 150)}...`;
        }
        return text;
    };

    return (
        <div className="game-news-section">
            <div className="game-news-section__header">
                <h2>Latest News</h2>
                <a
                    href={`https://store.steampowered.com/app/${game.steamAppId}/news/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="game-news-section__view-all"
                >
                    View All
                </a>
            </div>

            <div className="game-news-section__grid">
                {newsItems.slice(0, 4).map((item, index) =>
                    <a
                        key={item.gid || index}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="game-news-section__card"
                    >
                        <div className="game-news-section__card-content">
                            <div className="game-news-section__card-header">
                                <FaNewspaper className="game-news-section__card-icon" />
                                {item.feedlabel &&
                                    <span className="game-news-section__feed-label">{item.feedlabel}</span>
                                }
                            </div>
                            <h4 className="game-news-section__card-title">
                                {item.title}
                                {item.is_external_url && <FaExternalLinkAlt className="game-news-section__external-icon" />}
                            </h4>
                            <p className="game-news-section__card-excerpt">
                                {cleanContent(item.contents)}
                            </p>
                            <div className="game-news-section__card-meta">
                                <span className="game-news-section__card-time">
                                    <FaClock /> {formatTimeAgo(item.date)}
                                </span>
                                {item.author && item.author !== "Valve" &&
                                    <span className="game-news-section__card-author">by {item.author}</span>
                                }
                            </div>
                        </div>
                    </a>)}
            </div>
        </div>
    );
};

export default GameNewsSection;